(() => {
  if (window.skuSearch) return;
  const configElement = document.getElementById('sku-search-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const ttl = 20 * 60 * 1000;
  const listeners = new Set();
  let index = new Map();
  let loading = null;
  let complete = false;
  let completedAt = 0;
  let failed = false;
  let panelId = 0;

  const suffix = (query) => /^-?(\d{5})$/.exec(query.trim())?.[1] || null;
  const cacheKey = () => `sku-search:v1:${config.language}:${config.country}:${window.Shopify?.theme?.id || ''}:${config.asset}`;
  const notify = () => listeners.forEach((listener) => listener());
  const productKey = (url) => new URL(url, window.location.origin).pathname;

  function addRecord(record) {
    const digits = String(record.sku || '').replace(/\D/g, '');
    if (digits.length < 5) return;
    const key = digits.slice(-5);
    if (!index.has(key)) index.set(key, new Map());
    const products = index.get(key);
    const identity = productKey(record.url);
    if (!products.has(identity)) products.set(identity, record);
  }

  function readCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey()));
      if (!cached || !Array.isArray(cached.records) || Date.now() - cached.at >= ttl || cached.at > Date.now()) return false;
      cached.records.forEach(addRecord);
      completedAt = cached.at;
      complete = true;
      return true;
    } catch (error) {
      index = new Map();
      return false;
    }
  }

  function saveCache() {
    try {
      const records = Array.from(index.values()).flatMap((products) => Array.from(products.values()));
      sessionStorage.setItem(cacheKey(), JSON.stringify({ at: completedAt, records }));
    } catch (error) {
      // The completed in-memory index remains usable if storage is unavailable or full.
    }
  }

  async function fetchPage(path, page = 1, parameter = 'page') {
    const url = new URL(path, window.location.origin);
    url.search = '';
    url.searchParams.set('section_id', 'sku-search-index');
    url.searchParams.set(parameter, page);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('SKU index request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = document.querySelector('[data-sku-index]');
      if (!payload) throw new Error('SKU index is unavailable');
      const data = JSON.parse(payload.textContent);
      if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.page !== page || data.total > 25000) {
        throw new Error('SKU index pagination is incomplete');
      }
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  function load() {
    if (loading) return loading;
    if (complete && Date.now() - completedAt < ttl) return Promise.resolve();
    complete = false;
    failed = false;
    index = new Map();
    if (readCache()) return Promise.resolve();

    loading = (async () => {
      const tasks = [];
      const variantRequests = new Set();
      let nextTask = 0;
      let stopped = false;
      const ingest = (data, path, variantPage = false) => {
        data.products.forEach((product) => {
          product.variants.forEach((variant) => addRecord({
            title: product.title,
            url: product.url,
            image: product.image,
            type: product.type,
            sku: variant.sku,
            variantId: variant.id
          }));
          // Liquid exposes only the first variant page on collection products.
          if (!variantPage && product.more_variants && !variantRequests.has(product.url)) {
            variantRequests.add(product.url);
            tasks.push({ path: product.url, page: 1, parameter: 'page', variantPage: true });
          }
        });
        if (data.page === 1) {
          for (let page = 2; page <= data.pages; page += 1) {
            tasks.push({ path, page, parameter: data.parameter || 'page', variantPage });
          }
        }
        notify();
      };
      try {
        // Discover pagination first; all subsequent requests share these three workers.
        ingest(await fetchPage(config.url), config.url);
        const worker = async () => {
          while (!stopped && nextTask < tasks.length) {
            const task = tasks[nextTask++];
            try {
              ingest(await fetchPage(task.path, task.page, task.parameter), task.path, task.variantPage);
            } catch (error) {
              stopped = true;
              throw error;
            }
          }
        };
        const outcomes = await Promise.allSettled([worker(), worker(), worker()]);
        if (outcomes.some((outcome) => outcome.status === 'rejected')) throw new Error('SKU index is incomplete');
        complete = true;
        completedAt = Date.now();
        saveCache();
      } catch (error) {
        failed = true;
      } finally {
        loading = null;
        notify();
      }
    })();
    return loading;
  }

  function matches(query, types = []) {
    const products = index.get(suffix(query));
    if (!products) return [];
    return Array.from(products.values())
      .filter((product) => !types.length || types.includes(product.type))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  function variantUrl(product) {
    const url = new URL(product.url, window.location.origin);
    if (url.origin !== window.location.origin) return '#';
    url.searchParams.set('variant', product.variantId);
    return `${url.pathname}${url.search}`;
  }

  function createPanel() {
    const panel = document.createElement('section');
    panel.className = 'sku-search';
    const heading = document.createElement('h2');
    heading.className = 'sku-search__heading';
    heading.id = `sku-search-heading-${++panelId}`;
    heading.textContent = 'Coincidencias por SKU';
    panel.setAttribute('aria-labelledby', heading.id);
    const status = document.createElement('p');
    status.className = 'sku-search__status';
    status.setAttribute('role', 'status');
    const list = document.createElement('ul');
    list.className = 'sku-search__list';
    list.setAttribute('role', 'list');
    panel.append(heading, status, list);
    return panel;
  }

  function render(panel, query, types = [], nativeRoot = null) {
    const found = matches(query, types);
    const nativeProducts = new Set();
    if (nativeRoot) {
      const byPath = new Map(found.map((product) => [productKey(product.url), product]));
      nativeRoot.querySelectorAll('a[href]').forEach((link) => {
        if (link.closest('.sku-search')) return;
        const key = productKey(link.href);
        if (byPath.has(key)) {
          nativeProducts.add(key);
          link.href = variantUrl(byPath.get(key));
        }
      });
    }
    const status = panel.querySelector('.sku-search__status');
    const pending = !complete && !failed;
    status.dataset.pending = String(pending);
    status.textContent = pending ? 'Buscando productos por SKU…'
      : failed ? 'No se pudo completar la búsqueda por SKU. Intenta nuevamente.'
        : found.length ? '' : 'No se encontraron coincidencias por SKU.';
    status.hidden = !status.textContent;
    panel.setAttribute('aria-busy', String(pending));
    const list = panel.querySelector('.sku-search__list');
    const fragment = document.createDocumentFragment();
    found.forEach((product, position) => {
      if (nativeProducts.has(productKey(product.url))) return;
      const item = document.createElement('li');
      item.className = 'predictive-search__item';
      item.id = `${panel.querySelector('h2').id}-item-${position}`;
      const link = document.createElement('a');
      link.className = 'sku-search__link predictive-result focus-inset js-search-link';
      link.href = variantUrl(product);
      if (product.image && /^(https?:)?\//.test(product.image)) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = '';
        image.className = 'sku-search__image';
        image.loading = 'lazy';
        image.width = 56;
        image.height = 72;
        link.append(image);
      }
      const info = document.createElement('span');
      const title = document.createElement('span');
      title.className = 'sku-search__title';
      title.textContent = product.title;
      const sku = document.createElement('span');
      sku.className = 'sku-search__sku text-sm';
      sku.textContent = product.sku;
      info.append(title, sku);
      link.append(info);
      item.append(link);
      fragment.append(item);
    });
    list.replaceChildren(fragment);
    if (complete && found.length > 0 && !list.children.length) {
      status.textContent = 'Las coincidencias por SKU se muestran en los resultados de productos.';
      status.hidden = false;
    }
    return { pending, failed, count: found.length };
  }

  window.skuSearch = {
    compatible: (query) => !!suffix(query),
    createPanel,
    render,
    subscribe(listener) {
      listeners.add(listener);
      load();
      listener();
      return () => listeners.delete(listener);
    }
  };

  if (!customElements.get('sku-search-page')) {
    customElements.define('sku-search-page', class extends HTMLElement {
      connectedCallback() {
        this.refresh = this.refresh.bind(this);
        document.addEventListener('on:facet-filters:updated', this.refresh);
        window.addEventListener('popstate', this.refresh);
        this.refresh();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        document.removeEventListener('on:facet-filters:updated', this.refresh);
        window.removeEventListener('popstate', this.refresh);
      }

      refresh() {
        this.unsubscribe?.();
        const params = new URLSearchParams(window.location.search);
        const query = this.dataset.query || params.get('q') || '';
        this.hidden = !suffix(query);
        if (this.hidden) return;
        const panel = createPanel();
        this.replaceChildren(panel);
        const types = params.getAll('filter.p.product_type').filter(Boolean);
        const update = () => {
          if (!this.isConnected) return;
          const scope = this.closest('.cc-main-search');
          const state = render(panel, query, types, scope?.querySelector('#filter-results'));
          scope?.querySelectorAll('[data-sku-native-empty]').forEach((message) => {
            message.hidden = state.pending || state.failed || state.count > 0;
          });
        };
        this.unsubscribe = window.skuSearch.subscribe(update);
      }
    });
  }
})();
